/**
 * Template Build Chat Actions Hook
 *
 * Custom hook for managing template build chat interactions.
 * Provides actions for sending messages, responding to interrupts,
 * and selecting sections for context.
 */

import { useCallback, useState } from 'react';
import { generateRunId } from '@features/ai-chat-core';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import {
  addUserMessage,
  clearInterrupt,
  selectSection as selectSectionAction,
} from '../store/chatSlice';
import {
  selectInterrupt,
  selectInterruptType,
  selectSelectedSectionId,
  selectThreadId,
} from '../store/selectors';
import { useStreamTemplateBuildChatQuery } from '../store/streamingApi';
import type { TemplateBuildChatRequest } from '../types';

/**
 * Hook for managing template build chat actions
 *
 * Provides actions for:
 * - Sending text messages (AG-UI message format)
 * - Responding to selection interrupt (AG-UI resume format)
 * - Responding to confirmation interrupt (AG-UI resume format)
 * - Selecting sections for editing context
 *
 * @param taskId - The template build task ID
 * @param isReady - Whether the feature is ready (middleware registered). Defaults to true.
 */
export const useTemplateBuildActions = (taskId: string | null, isReady: boolean = true) => {
  const dispatch = useAppDispatch();
  const threadId = useAppSelector(selectThreadId);
  const interrupt = useAppSelector(selectInterrupt);
  const interruptType = useAppSelector(selectInterruptType);
  const selectedSectionId = useAppSelector(selectSelectedSectionId);

  const [currentPayload, setCurrentPayload] = useState<TemplateBuildChatRequest | null>(null);

  // Use RTK Query streaming with skip control
  const { data, isLoading, error } = useStreamTemplateBuildChatQuery(
    currentPayload as TemplateBuildChatRequest,
    {
      skip: !isReady || !currentPayload || !taskId,
    },
  );

  /**
   * Send text message (initial or continuation)
   */
  const sendMessage = useCallback(
    (message: string) => {
      if (!taskId) return;

      dispatch(addUserMessage(message));

      const payload: TemplateBuildChatRequest = {
        taskId,
        threadId: threadId || null,
        runId: generateRunId(),
        messages: [
          {
            id: crypto.randomUUID(),
            role: 'user',
            content: message,
          },
        ],
      };

      // Include selected section if available
      if (selectedSectionId) {
        payload.selected_section_id = selectedSectionId;
      }

      console.debug('📤 [Chat] Sending message with selectedSectionId:', selectedSectionId);
      setCurrentPayload(payload);
    },
    [dispatch, taskId, threadId, selectedSectionId],
  );

  /**
   * Select a section for editing context
   */
  const selectSection = useCallback(
    (sectionId: string | null) => {
      dispatch(selectSectionAction(sectionId));
    },
    [dispatch],
  );

  /**
   * Respond to selection interrupt
   * Note: Resume payloads must NOT include messages (mutually exclusive)
   */
  const selectOptions = useCallback(
    (
      selectedIds: string[] | Record<string, { selected_ids?: string[]; confirmed?: boolean }>,
    ) => {
      if (!interrupt || !currentPayload || !taskId) return;

      const resumePayload =
        typeof selectedIds === 'object' && !Array.isArray(selectedIds)
          ? selectedIds
          : { selected_ids: selectedIds };

      // Create resume payload without messages (they are mutually exclusive)
      setCurrentPayload({
        taskId,
        threadId,
        runId: generateRunId(),
        selected_section_id: selectedSectionId ?? undefined,
        resume: {
          interruptId: interrupt.interruptId,
          payload: resumePayload,
        },
      });
    },
    [interrupt, currentPayload, taskId, threadId, selectedSectionId],
  );

  /**
   * Respond to confirmation interrupt
   * Note: Resume payloads must NOT include messages (mutually exclusive)
   */
  const submitConfirmation = useCallback(
    (confirmed: boolean) => {
      if (!interrupt || !currentPayload || !taskId) return;

      // Create resume payload without messages (they are mutually exclusive)
      setCurrentPayload({
        taskId,
        threadId,
        runId: generateRunId(),
        selected_section_id: selectedSectionId ?? undefined,
        resume: {
          interruptId: interrupt.interruptId,
          payload: { confirmed },
        },
      });
    },
    [interrupt, currentPayload, taskId, threadId, selectedSectionId],
  );

  /**
   * Submit text response to interrupt
   * Note: Resume payloads must NOT include messages (mutually exclusive)
   */
  const submitTextResponse = useCallback(
    (text: string) => {
      if (!interrupt || !currentPayload || !taskId) return;

      // Create resume payload without messages (they are mutually exclusive)
      setCurrentPayload({
        taskId,
        threadId,
        runId: generateRunId(),
        selected_section_id: selectedSectionId ?? undefined,
        resume: {
          interruptId: interrupt.interruptId,
          payload: { text },
        },
      });
    },
    [interrupt, currentPayload, taskId, threadId, selectedSectionId],
  );

  /**
   * Cancel the current interrupt without sending a response
   */
  const cancelInterrupt = useCallback(() => {
    dispatch(clearInterrupt());
  }, [dispatch]);

  return {
    sendMessage,
    selectSection,
    selectOptions,
    submitConfirmation,
    submitTextResponse,
    cancelInterrupt,
    isStreaming: data?.isActive || false,
    isLoading,
    error,
    /** Current interrupt type if active */
    interruptType,
    /** Whether an interrupt is currently active */
    hasActiveInterrupt: Boolean(interrupt?.isActive),
  };
};

